import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FilterBar = ({ 
  categories, 
  technologies, 
  selectedCategory, 
  selectedTechnology, 
  searchQuery,
  onCategoryChange, 
  onTechnologyChange, 
  onSearchChange,
  onClearFilters,
  projectCount 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasActiveFilters = selectedCategory !== 'All' || selectedTechnology !== 'All' || searchQuery;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl p-6 shadow-3d border border-border/50"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Filter" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Filter Projects</h3>
            <p className="text-sm text-muted-foreground">{projectCount} projects found</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={onClearFilters}
              className="text-muted-foreground hover:text-foreground"
            >
              Clear
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground hover:text-foreground lg:hidden"
          />
        </div>
      </div>
      {/* Search Bar */}
      <div className="mb-6">
        <Input
          type="search"
          placeholder="Search projects by name, description, or technology..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Filters */}
      <AnimatePresence>
        {(isExpanded || window.innerWidth >= 1024) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="space-y-6"
          >
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Project Category
              </label>
              <div className="flex flex-wrap gap-2">
                {categories?.map((category) => (
                  <motion.button
                    key={category}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onCategoryChange(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      selectedCategory === category
                        ? 'bg-accent text-accent-foreground shadow-accent'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {category}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Technology Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Technology Stack
              </label>
              <div className="flex flex-wrap gap-2">
                {technologies?.map((tech) => (
                  <motion.button
                    key={tech}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onTechnologyChange(tech)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-300 flex items-center space-x-2 ${
                      selectedTechnology === tech
                        ? 'bg-accent/20 text-accent border border-accent/30' :'bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground border border-transparent'
                    }`}
                  >
                    <Icon name="Code" size={12} />
                    <span>{tech}</span>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="pt-4 border-t border-border/50"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Active Filters:</span>
                  <div className="flex items-center space-x-2">
                    {selectedCategory !== 'All' && (
                      <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-md">
                        {selectedCategory}
                      </span>
                    )}
                    {selectedTechnology !== 'All' && (
                      <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-md">
                        {selectedTechnology}
                      </span>
                    )}
                    {searchQuery && (
                      <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-md">
                        "{searchQuery}"
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FilterBar;